import type { QuestionTranslateOptionSchema, QuestionTranslateSchema } from '@repo/schemas/quiz/questionTranslate';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { questionTranslateSchema } from '@repo/schemas/quiz/questionTranslate';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

const DEFAULT_OPTION_ALERT_STATE = {
  isOpen: false,
  optionIndex: -1,
  optionId: -1,
};

export function useQuestionTranslateForm(data?: QuestionTranslateSchema) {
  const [inputValue, setInputValue] = useState<string>('');
  const [selectedOptions, setSelectedOptions] = useState<QuestionTranslateOptionSchema[]>([]);
  const [deleteOptionAlert, setDeleteOptionAlert] = useState<{
    isOpen: boolean;
    optionIndex: number;
    optionId: number;
  }>(DEFAULT_OPTION_ALERT_STATE);

  const form = useForm<QuestionTranslateSchema>({
    defaultValues: data,
    mode: 'onChange',
    resolver: standardSchemaResolver(questionTranslateSchema),
  });

  //trigger validation on component mount
  useEffect(() => {
    form.trigger();
  }, []);

  const translateQuestionOptions = useFieldArray({
    control: form.control,
    keyName: 'fieldId',
    name: 'options',
  });

  const translateQuestionAnswers = useFieldArray({
    control: form.control,
    keyName: 'fieldId',
    name: 'answers',
  });

  const isAddControlsDisabled: boolean = !inputValue.trim();

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const handleAddOption = () => {
    if (!inputValue.trim()) return;

    translateQuestionOptions.append({
      id: translateQuestionOptions.fields.length + 1,
      content: inputValue,
    });
    setInputValue('');
  };

  const handleRemoveOption = (index: number) => {
    const answersFlattened: number[] = form.getValues('answers').flat();
    const options = form.getValues('options');
    const optionId: number | undefined = options[index]?.id;

    if (optionId && answersFlattened.includes(optionId)) {
      setDeleteOptionAlert({
        isOpen: true,
        optionIndex: index,
        optionId: optionId,
      });
      return;
    }

    executeOptionRemoval(index);
  };

  const executeOptionRemoval = (index: number) => {
    const optionId: number | undefined = translateQuestionOptions.fields[index]?.id;

    if (optionId === undefined) return;

    translateQuestionOptions.remove(index);
    setSelectedOptions((prev) => prev.filter((option) => option.id !== optionId));
  };

  const handleConfirmDeleteOption = () => {
    const { optionIndex, optionId } = deleteOptionAlert;

    executeOptionRemoval(optionIndex);

    const currentAnswers: number[][] = form.getValues('answers');
    const filteredAnswers: number[][] = currentAnswers.filter((answer) => !answer.includes(optionId));

    form.setValue('answers', filteredAnswers);
    setDeleteOptionAlert(DEFAULT_OPTION_ALERT_STATE);
  };

  const handleCancelDeleteOption = () => {
    setDeleteOptionAlert({
      isOpen: false,
      optionIndex: -1,
      optionId: -1,
    });
  };

  const handleSelectOption = (option: QuestionTranslateOptionSchema) => {
    const isAlreadySelected = selectedOptions.includes(option);

    if (isAlreadySelected) {
      setSelectedOptions((prev) => prev.filter((item) => item !== option));
    } else {
      setSelectedOptions((prev) => [...prev, option]);
    }
  };

  const handleRemoveSelectedOption = (index: number) => {
    setSelectedOptions((prev) => prev.filter((_, selectedIndex) => selectedIndex !== index));
  };

  const handleClearSelectedOptions = () => {
    setSelectedOptions([]);
  };

  const handleCreateAnswer = () => {
    if (!selectedOptions.length) return;

    const currentAnswers: number[][] = form.getValues('answers');

    const newAnswer: number[] = selectedOptions.map((option) => option.id);

    form.setValue('answers', [...currentAnswers, newAnswer]);

    setSelectedOptions([]);
  };

  const handleCopyAnswer = (options: QuestionTranslateOptionSchema[]) => {
    setSelectedOptions(options);
  };

  return {
    // State
    inputValue,
    selectedOptions,
    isAddControlsDisabled,
    deleteOptionAlert,

    //Form
    form,

    // Form arrays
    translateQuestionOptions,
    translateQuestionAnswers,

    // Handlers
    handleInputChange,
    handleAddOption,
    handleSelectOption,
    handleRemoveOption,
    handleRemoveSelectedOption,
    handleClearSelectedOptions,
    handleCreateAnswer,
    handleCopyAnswer,
    handleConfirmDeleteOption,
    handleCancelDeleteOption,
  };
}
